import React, { useState } from 'react';
import {
  Box,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  useTheme,
} from '@mui/material';
import PaletteIcon from '@mui/icons-material/Palette';
import CloseIcon from '@mui/icons-material/Close';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { alpha } from '@mui/material/styles';
import { borderRadius, getControlBorderColor } from '../../../theme/theme';
import ActionHelpTooltip from '../../../shared/ui/common/ActionHelpTooltip';
import { ConfirmationDialog } from '../../../shared/ui/dialogs/ConfirmationDialog';
import {
  EXPORT_ROW_HIGHLIGHT_OPTIONS,
  getExportRowHighlightOption,
  type ExportRowHighlightColor,
} from '../lib/rowHighlights';
import { EXPORT_RAIL_CONTROL_SIZE } from './exportRailControlStyles';

const RowHighlightColorHelp = {
  kind: 'action' as const,
  title: 'Mark row color',
  summary: 'Apply or clear a color marker for the selected rows in the export review table.',
  downstream: 'The selected color is saved for the current Revit model and remains until you clear or change it.',
  note: 'The color marker affects only the review table. It does not modify Revit elements or PCF output.',
};

interface RowHighlightColorMenuProps {
  buttonLabel: string;
  clearLabel: string;
  menuLabel: string;
  menuItemLabel: (colorLabel: string) => string;
  resetConfirmMessage: string;
  resetLabel: string;
  value: ExportRowHighlightColor | null;
  showClear?: boolean;
  showReset?: boolean;
  disabled?: boolean;
  colorOptionsDisabled?: boolean;
  compact?: boolean;
  buttonRef?: React.Ref<HTMLButtonElement>;
  onChange: (color: ExportRowHighlightColor | null) => void;
  onReset?: () => void;
}

const RowHighlightKeyshortcuts = [
  ...EXPORT_ROW_HIGHLIGHT_OPTIONS.map((option) => `Alt+${option.shortcutKey}`),
  'Alt+0',
].join(' ');

function formatShortcutTooltip(label: string, shortcut: string): string {
  return `${label.charAt(0).toUpperCase()}${label.slice(1)} - ${shortcut}`;
}

export default function RowHighlightColorMenu({
  buttonLabel,
  clearLabel,
  menuLabel,
  menuItemLabel,
  resetConfirmMessage,
  resetLabel,
  value,
  showClear = false,
  showReset = false,
  disabled = false,
  colorOptionsDisabled = false,
  compact = false,
  buttonRef,
  onChange,
  onReset,
}: RowHighlightColorMenuProps) {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [resetConfirmOpen, setResetConfirmOpen] = useState(false);
  const selectedOption = getExportRowHighlightOption(value);
  const open = Boolean(anchorEl);
  const size = compact ? EXPORT_RAIL_CONTROL_SIZE : 30;

  const closeMenu = () => setAnchorEl(null);
  const closeResetConfirm = () => setResetConfirmOpen(false);

  return (
    <>
      <ActionHelpTooltip help={RowHighlightColorHelp}>
        <IconButton
          ref={buttonRef}
          size="small"
          disabled={disabled}
          aria-label={buttonLabel}
          aria-haspopup="menu"
          aria-expanded={open ? 'true' : undefined}
          aria-keyshortcuts={RowHighlightKeyshortcuts}
          onClick={(event) => {
            event.stopPropagation();
            if (disabled) {
              return;
            }
            setAnchorEl(event.currentTarget);
          }}
          onKeyDown={(event) => {
            event.stopPropagation();
          }}
          sx={{
            width: size,
            height: size,
            borderRadius: `${borderRadius.sm}px`,
            color: disabled
              ? theme.palette.action.disabled
              : selectedOption ? selectedOption.main : theme.palette.text.secondary,
            border: compact
              ? 'none'
              : `1px solid ${selectedOption ? alpha(selectedOption.main, 0.62) : getControlBorderColor(theme)}`,
            backgroundColor: selectedOption
              ? alpha(selectedOption.main, theme.palette.mode === 'light' ? 0.16 : 0.24)
              : compact
                ? 'transparent'
                : alpha(theme.palette.background.paper, theme.palette.mode === 'light' ? 0.72 : 0.28),
            '&:hover': {
              backgroundColor: selectedOption
                ? alpha(selectedOption.main, theme.palette.mode === 'light' ? 0.24 : 0.34)
                : alpha(theme.palette.primary.main, 0.08),
            },
          }}
        >
          {selectedOption ? (
            <Box
              aria-hidden="true"
              sx={{
                width: 12,
                height: 12,
                borderRadius: '50%',
                backgroundColor: selectedOption.main,
              }}
            />
          ) : (
            <PaletteIcon sx={{ fontSize: compact ? '1rem' : '1.15rem' }} />
          )}
        </IconButton>
      </ActionHelpTooltip>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={(event: object) => {
          if ('stopPropagation' in event && typeof event.stopPropagation === 'function') {
            event.stopPropagation();
          }
          closeMenu();
        }}
        MenuListProps={{
          'aria-label': menuLabel,
          dense: true,
          onClick: (event) => event.stopPropagation(),
          sx: {
            display: 'flex',
            alignItems: 'center',
            gap: 0.25,
            px: 0.5,
            py: 0.5,
          },
        }}
      >
        {EXPORT_ROW_HIGHLIGHT_OPTIONS.map((option) => {
          const shortcut = `Alt+${option.shortcutKey}`;
          return (
            <MenuItem
              key={option.id}
              selected={option.id === value}
              disabled={colorOptionsDisabled}
              aria-label={`${menuItemLabel(option.label)}, ${shortcut}`}
              onClick={(event) => {
                event.stopPropagation();
                onChange(option.id);
                closeMenu();
              }}
              sx={{
                minWidth: 0,
                minHeight: 30,
                borderRadius: `${borderRadius.md}px`,
                px: 0.75,
              }}
            >
              <Tooltip
                title={formatShortcutTooltip(option.label, shortcut)}
                arrow
                placement="top"
                enterDelay={0}
                leaveDelay={0}
              >
                <Box
                  component="span"
                  aria-hidden="true"
                  data-testid={`row-highlight-color-swatch-${option.id}`}
                  sx={{
                    width: 16,
                    height: 16,
                    borderRadius: '50%',
                    backgroundColor: option.main,
                  }}
                />
              </Tooltip>
            </MenuItem>
          );
        })}
        {showClear || value ? (
          <MenuItem
            aria-label={`${clearLabel}, Alt+0`}
            onClick={(event) => {
              event.stopPropagation();
              onChange(null);
              closeMenu();
            }}
            sx={{
              minWidth: 0,
              minHeight: 30,
              borderRadius: `${borderRadius.md}px`,
              px: 0.75,
            }}
          >
            <Tooltip
              title="Clear - Alt+0"
              arrow
              placement="top"
              enterDelay={0}
              leaveDelay={0}
            >
              <CloseIcon data-testid="row-highlight-clear-swatch" sx={{ fontSize: '1rem' }} />
            </Tooltip>
          </MenuItem>
        ) : null}
        {showReset && onReset ? (
          <MenuItem
            aria-label={resetLabel}
            onClick={(event) => {
              event.stopPropagation();
              closeMenu();
              setResetConfirmOpen(true);
            }}
            sx={{
              minWidth: 0,
              minHeight: 30,
              borderRadius: `${borderRadius.md}px`,
              px: 0.75,
            }}
          >
            <Tooltip
              title="Reset highlights"
              arrow
              placement="top"
              enterDelay={0}
              leaveDelay={0}
            >
              <RestartAltIcon sx={{ fontSize: '1rem' }} />
            </Tooltip>
          </MenuItem>
        ) : null}
      </Menu>
      <ConfirmationDialog
        open={resetConfirmOpen}
        title="Reset row highlights"
        message={resetConfirmMessage}
        cancelLabel="Keep colors"
        confirmLabel="Reset colors"
        destructive
        cancelAriaLabel="Keep row highlight colors"
        confirmAriaLabel={resetLabel}
        onCancel={closeResetConfirm}
        onConfirm={() => {
          onReset?.();
          closeResetConfirm();
        }}
        aria-labelledby="export-row-highlight-reset-title"
      />
    </>
  );
}
